import { useEffect, useRef, useState } from "react";
import { ImagePlus } from "lucide-react";
import type { AssetRef, BlogIndex, BuilderApi, PostPhoto } from "../api/contract";
import { PhotoError, preparePhoto } from "../lib/photos";
import { Spinner } from "./Spinner";
import { requestTimeout } from "../lib/requestTimeout";
import { LoadingActivity } from "./area/LoadingState";

/** A photo that already belongs to the post, with the description it has where it is used. */
export interface PostPhotoChoice { assetId: string; version: number; src: string; alt: string }
export type SitePhoto = BlogIndex["photos"][number];

/** What the owner picked. Site photos and uploads still have to be made the post's own. */
export type PhotoPick =
  | { from: "post"; photo: PostPhotoChoice }
  | { from: "site"; photo: SitePhoto }
  | { from: "upload"; photo: PostPhotoChoice };

export const COPIED_NOTE = "Copied into this post. Changes to the site photo won't affect it.";

/** Uploads straight into the post: the same upload, resizing and description steps as the site's photos, but never added to Content. */
function PostPhotoUpload({ api, siteId, disabled, onAdded }: { api: BuilderApi; siteId: string; disabled: boolean; onAdded: (photo: PostPhotoChoice) => void | Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [alt, setAlt] = useState("");
  const [describing, setDescribing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const typed = useRef(false);
  const job = useRef<Promise<AssetRef> | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const choose = (chosen: File | undefined) => {
    if (!chosen || working || uploading) return;
    setError(null); setAlt(""); typed.current = false;
    if (chosen.type === "image/svg+xml" || /\.svg$/i.test(chosen.name)) { setFile(null); setError("Posts can use JPEG, PNG or WebP photos."); return; }
    setFile(chosen);
    setUploading(true);
    setDescribing(true);
    const prepared = preparePhoto(chosen);
    const upload = requestTimeout(async () => { const { photo } = await prepared; return api.uploadPhoto(siteId, photo); }, undefined, 60_000);
    job.current = upload;
    // The same look step as a site photo; it only suggests words, so a failure just leaves the field to the owner.
    void requestTimeout(async () => { const [{ aiCopy }, asset] = await Promise.all([prepared, upload]);
      const seen = aiCopy ? await api.uploadPhoto(siteId, aiCopy).catch(() => null) : null;
      return await api.describePhoto(siteId, seen ?? asset);
    }, undefined, 60_000).then((look) => { if (job.current === upload && !typed.current) setAlt((current) => current.trim() ? current : look.description); })
      .catch(() => undefined).finally(() => { if (job.current === upload) setDescribing(false); });
    void upload.catch((caught: unknown) => { if (job.current === upload) setError(caught instanceof PhotoError ? caught.message : "This photo couldn't be uploaded. Try again."); }).finally(() => { if (job.current === upload) setUploading(false); });
  };

  const submit = async () => {
    if (!job.current || !alt.trim() || working) return;
    setWorking(true); setError(null);
    try {
      const asset = await job.current;
      const added: PostPhoto = await requestTimeout(() => api.addPostPhoto(siteId, { asset }), undefined, 60_000);
      await onAdded({ assetId: added.assetId, version: added.version, src: added.src, alt: alt.trim() });
      setOpen(false); setFile(null); setAlt(""); job.current = null;
    } catch { setError("This photo couldn't be added. Try again."); }
    finally { setWorking(false); }
  };

  if (!open) return <button type="button" className="bw-btn bw-btn--quiet bw-blog-upload-open" disabled={disabled} onClick={() => setOpen(true)}><ImagePlus size={16} aria-hidden="true" /> Upload a photo</button>;
  return <div className="bw-blog-upload" aria-busy={uploading || working}>
    <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(event) => { choose(event.target.files?.[0]); event.target.value = ""; }} />
    <div className="bw-blog-upload-pick">
      {preview ? <img src={preview} alt="" /> : <button type="button" className="bw-blog-upload-empty" disabled={disabled || working || uploading} onClick={() => input.current?.click()}><ImagePlus size={20} aria-hidden="true" /><span>Choose a photo</span></button>}
      {preview && <button type="button" className="bw-btn bw-btn--text" disabled={disabled || working || uploading} onClick={() => input.current?.click()}>Change</button>}
    </div>
    {file && <label className="bw-area-field">What this photo shows
      <input value={alt} maxLength={200} placeholder="e.g. Two-tier wedding cake with roses" aria-busy={describing} onChange={(event) => { typed.current = true; setAlt(event.target.value); }} />
      {uploading ? <LoadingActivity label="Uploading photo…" /> : describing && !alt.trim() && <LoadingActivity label="Describing photo…" />}
    </label>}
    <small className="bw-area-hint">It goes into this post only, not into your site's photos.</small>
    {error && <p role="alert" className="bw-blog-menu-note">{error}</p>}
    <div className="bw-actions">
      <button type="button" className="bw-btn bw-btn--primary" disabled={!file || !alt.trim() || working || uploading || disabled} onClick={() => void submit()}>{working && <Spinner />}Add to post</button>
      <button type="button" className="bw-btn bw-btn--quiet" disabled={working} onClick={() => { setOpen(false); setFile(null); setError(null); setUploading(false); setDescribing(false); job.current = null; }}>Cancel</button>
    </div>
  </div>;
}

/**
 * Two sources and an upload. "This post" are photos the post already uses (picking one again
 * copies nothing). "From your site's photos" are copied into the post: after that the post doesn't
 * depend on the site photo, and the picker says so.
 */
export function PostPhotoPicker({ api, siteId, postPhotos, sitePhotos, selected, radio, busy, onPick, leading }: {
  api: BuilderApi;
  siteId: string;
  postPhotos: PostPhotoChoice[];
  sitePhotos: SitePhoto[];
  /** The photo currently chosen (a cover), marked in "This post". */
  selected?: { assetId: string; version: number } | undefined;
  /** Photos are a single choice (a cover), not something to insert. */
  radio?: boolean;
  busy: boolean;
  onPick: (pick: PhotoPick) => void | Promise<void>;
  /** An extra first choice in "This post", such as "None" for a cover. */
  leading?: React.ReactNode;
}) {
  return <div className="bw-blog-picker" aria-busy={busy}>
    {busy && <LoadingActivity label="Adding photo to post…" />}
    <p className="bw-blog-picker-head">This post</p>
    {postPhotos.length || leading
      ? <div className="bw-blog-photo-grid" role={radio ? "radiogroup" : "group"} aria-label="Photos in this post">{leading}{postPhotos.map((photo) => <button key={`${photo.assetId}/${photo.version}`} type="button" role={radio ? "radio" : undefined} aria-checked={radio ? selected?.assetId === photo.assetId && selected.version === photo.version : undefined} disabled={busy} title={photo.alt} onClick={() => void onPick({ from: "post", photo })}><img src={photo.src} alt={photo.alt} /></button>)}</div>
      : <small className="bw-area-hint">No photos in this post yet.</small>}
    <p className="bw-blog-picker-head">From your site's photos</p>
    {sitePhotos.length
      ? <><div className="bw-blog-photo-grid" role="group" aria-label="Your site's photos">{sitePhotos.map((photo) => <button key={photo.id} type="button" disabled={busy} title={photo.alt} onClick={() => void onPick({ from: "site", photo })}><img src={photo.src} alt={photo.alt} /></button>)}</div>
        <small className="bw-area-hint">Picking one copies it into this post. Changes to the site photo won't affect it.</small></>
      : <small className="bw-area-hint">Your site has no photos yet.</small>}
    <PostPhotoUpload api={api} siteId={siteId} disabled={busy} onAdded={(photo) => onPick({ from: "upload", photo })} />
  </div>;
}
